import { get_attribute_value } from '/srv/web/wyvr/html/releases/58819/gen/client/shop/core/attributes.js?1733898722587';
import { load_products } from '/srv/web/wyvr/html/releases/58819/gen/client/shop/api-client/product/load_products.js?1733898722587';
import { context } from '/srv/web/wyvr/html/releases/58819/gen/client/wyvr/context.js?1733898722587';
import { logger, get_error_message } from '@wyvr/generator/universal.js';
import { variant_to_product } from '/srv/web/wyvr/html/releases/58819/gen/client/odoscope/config/variant_to_product.js?1733898722587';

/**
 * Sorts an array of products based on an odoscope decision result
 *
 * @param {Array} products - The array of products to be sorted.
 * @param {OdoscopeAnswer} decision - The decision object containing variants.
 * @param {boolean} [filtering=true] - Flag indicating if filtering has been applied to the products.
 * @param {function} update_fn - The function to update the products, gets called multiple times
 * @returns {OdoscopeSortedResult} An object with sorted products and a flag indicating if sorting was applied.
 */
export async function sort_products_by_decision(products, decision, filtering = true, update_fn = undefined) {
    const use_odoscope_products = true;
    const use_shop_data_for_products = !use_odoscope_products;

    if (use_shop_data_for_products && !context().products_cache) {
        context().products_cache = new Map();
    }
    const final_update_fn = update_fn || ((x) => x);
    const items_per_page = 11;

    // when no results gets returned it means the original sort order should be kept
    if (!filtering && (!Array.isArray(decision?.variants) || decision.variants.length === 0)) {
        return final_update_fn(products, false);
    }

    if (use_shop_data_for_products) {
        // the intitial products are already loaded
        setLoadedProductsToCache(products);
        for (const product of products) {
            const sku = get_attribute_value(product, 'sku');
            if (sku) {
                context().products_cache.set(sku, product);
            }
        }
    }

    // get all product skus from the decision
    const sorted_skus = [];
    const missing_products = [];
    if (use_shop_data_for_products) {
        for (const variant of decision.variants) {
            const sku = variant?.variantID;
            if (!sku) {
                continue;
            }
            sorted_skus.push(sku);
            // if the product is already in the cache, we don't need to load it
            if (context().products_cache.has(sku)) {
                continue;
            }
            missing_products.push(sku);
        }

        if (missing_products.length > 0) {
            // load the missing products of the first page
            const [first_page_error, first_loaded_products] = await load_products(missing_products.slice(0, items_per_page));
            if (first_page_error) {
                logger.error(get_error_message(first_page_error, import.meta.url, 'load first page products'));
                return final_update_fn(products, false);
            }
            setLoadedProductsToCache(first_loaded_products);
            const remaining_missing_products = missing_products.slice(items_per_page);
            if (remaining_missing_products.length > 0) {
                // update the products early to show the first page
                final_update_fn(getLoadedProductsFromCache(sorted_skus.slice(0, items_per_page)), true);

                // load the missing products of the remaining pages
                const [remaining_error, remaining_loaded_products] = await load_products(remaining_missing_products);
                if (remaining_error) {
                    logger.error(get_error_message(remaining_error, import.meta.url, 'load remaining pages products'));

                    // avoid updating in case of an error
                    return undefined;
                }
                setLoadedProductsToCache(remaining_loaded_products);
            }
        }
        return final_update_fn(getLoadedProductsFromCache(sorted_skus), true);
    }

    return final_update_fn(getProductsWithOdoscopeData(decision), true);
}

function getProductsWithOdoscopeData(decision) {
    const result = [];
    const master_id_index = {};
    //transform odoscope products
    const entries = Object.entries(variant_to_product);

    for (const variant of decision.variants) {
        let add_to_index = false;
        const master_id = variant?.variantInfo?.master_id;
        // group the variants by master_id
        if (master_id) {
            if (!master_id_index[master_id]) {
                master_id_index[master_id] = result.length;
            } else {
                add_to_index = master_id_index[master_id];
            }
        }
        // identify the product as odoscope product
        const product = {
            osc: true
        };
        for (const [key, config] of entries) {
            let emit = true;
            if (typeof config.emit === 'function') {
                emit = config.emit(variant);
            }
            if (!emit) {
                continue;
            }
            if (typeof config.value === 'function') {
                product[key] = config.value(variant);
            }
        }
        if (add_to_index === false) {
            // add the product as first entry
            if (master_id) {
                // make a shallow copy of the product in order to avoid updating the master product itself
                product.variants = [Object.assign({}, product)];
                // avoid updating the master product itself
                product.type_id = 'configurable';
                // master_id is normally the name of the product, so remove the differentiation of the simple
                if ((get_attribute_value(product, 'name') ?? '').indexOf(master_id) > -1) {
                    product.name = master_id;
                }
            }
            result.push(product);
        } else {
            if (product.type_id !== 'configurable') {
                result[add_to_index].variants.push(product);
            } else {
                // when a configurable product is found, we need to update the simple product, to look like a master product
                result[add_to_index].name = product.name;
                //result[add_to_index].url_key = product.url_key;

                result[add_to_index].master = get_attribute_value(product, 'sku');
            }
        }
    }

    // clean configurables without variants and add lowest price to the master product
    const cleaned_products = result.filter((product) => {
        const show = product.type_id === 'simple' || (product.type_id === 'configurable' && product.variants?.length > 0);
        if (!show) {
            return false;
        }
        // add lowest price to the master product
        if (product.type_id === 'configurable') {
            let lowest_price = Number.POSITIVE_INFINITY;
            let lowest_final_price = Number.POSITIVE_INFINITY;

            for (const variant of product.variants) {
                lowest_price = Math.min(lowest_price, get_attribute_value(variant, 'price'));
                lowest_final_price = Math.min(lowest_final_price, get_attribute_value(variant, 'final_price'));
            }
            product.price = lowest_price;
            product.final_price = lowest_final_price;
        }
        return true;
    });
    return cleaned_products;
}

/**
 * Sets the loaded products to the cache.
 * @param {Array} loaded_products - The array of loaded products.
 */
function setLoadedProductsToCache(loaded_products) {
    if (!Array.isArray(loaded_products)) {
        return;
    }
    const cache = context().products_cache;
    for (const product of loaded_products) {
        if (product) {
            cache.set(get_attribute_value(product, 'sku'), product);
        }
    }
}

/**
 * Retrieves loaded products from cache based on the provided SKUs.
 * @param {Array} skus - The SKUs of the products to retrieve from cache.
 * @returns {Array} - An array of loaded products from cache.
 */
function getLoadedProductsFromCache(skus) {
    if (!Array.isArray(skus)) {
        return [];
    }
    const cache = context().products_cache;
    return skus.map((sku) => cache.get(sku)).filter(Boolean);
}
